import dayjs from 'dayjs';
import { Edit, Ellipsis, ExternalLink, Trash2, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { toast } from 'sonner';

import { useDeleteBoard, type Board } from '@/entities/board';
import { BaseTable, type TableColumnT } from '@/shared/components/base-table';
import { Button } from '@/shared/components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/shadcn/dropdown-menu';
import { requestConfirm } from '@/shared/utils/request-confirm';
import { requestRenameBoardDialog } from './overlays/request-rename-board-dialog';
import { useUpdateBoardTitle } from '@/entities/board';

type Props = {
  boards: Board[];
  isLoading: boolean;
  onOpenBoard: (boardId: string) => void;
  onCreateBoardClick: () => void;
};

export function BoardsListTable({ boards, isLoading, onOpenBoard, onCreateBoardClick }: Props) {
  const deleteBoardMutation = useDeleteBoard();
  const updateBoardTitleMutation = useUpdateBoardTitle();

  const handleRequestDelete = async (boardId: string, boardTitle: string) => {
    await requestConfirm({
      title: 'Delete board?',
      description: (
        <>
          Are you sure you want to delete "{boardTitle}"?
          <br />
          This action cannot be undone.
        </>
      ),
      confirmButton: {
        children: 'Delete',
        variant: 'destructive',
      },
      onConfirm: async () => {
        await deleteBoardMutation.mutateAsync(boardId);
        toast.success('Board deleted');
      },
    });
  };

  const columns: TableColumnT<Board>[] = [
    {
      columnKey: 'board_name',
      Header: <>Board name</>,
      Cell(rowData) {
        return rowData.rowData.title;
      },
      className: 'flex-1',
    },
    {
      columnKey: 'posts',
      Header: <>Posts</>,
      Cell(rowData) {
        return String(rowData.rowData.post_count);
      },
      className: 'w-40',
    },
    {
      columnKey: 'updated_at',
      Header: <>Updated date</>,
      Cell(rowData) {
        return dayjs(rowData.rowData.updated_at).format('MMM DD, YYYY');
      },
      className: 'w-40',
    },
    {
      columnKey: 'actions',
      Header: (<></>) as ReactNode,
      Cell(rowData) {
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Ellipsis className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <ActionMenuItem
                icon={ExternalLink}
                label="View"
                onSelect={() => onOpenBoard(rowData.rowKey)}
              />
              <ActionMenuItem
                icon={Edit}
                label="Rename"
                onSelect={() =>
                  void requestRenameBoardDialog({
                    initialTitle: rowData.rowData.title,
                    onSave: async (title) => {
                      await updateBoardTitleMutation.mutateAsync({
                        boardId: rowData.rowKey,
                        title,
                      });
                      toast.success('Title updated');
                    },
                  })
                }
              />
              <DropdownMenuItem
                variant="destructive"
                onClick={() => void handleRequestDelete(rowData.rowKey, rowData.rowData.title)}
              >
                <Trash2 className="size-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
      className: 'w-20',
    },
  ];

  return (
    <BaseTable
      tableClassName="pt-2 h-full"
      columns={columns}
      data={boards.map((board) => ({
        rowKey: board.id,
        rowData: board,
      }))}
      onRowClick={onOpenBoard}
      isLoading={isLoading}
      placeholder={{
        node: (
          <div className="py-12 text-center">
            <p className="text-muted-foreground text-sm">
              No boards yet. Create your first board to organize saved posts.
            </p>
            <Button className="mt-4" onClick={onCreateBoardClick}>
              Create New Board
            </Button>
          </div>
        ),
      }}
    />
  );
}

type ActionMenuItemProps = {
  icon: LucideIcon;
  label: string;
  onSelect: () => void;
};

function ActionMenuItem({ icon: Icon, label, onSelect }: ActionMenuItemProps) {
  return (
    <DropdownMenuItem onClick={onSelect}>
      <Icon className="size-4" />
      {label}
    </DropdownMenuItem>
  );
}
